import { useQuery } from '@apollo/client';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  RuleCreateInput,
  RuleUpdateInput,
  supportedRuleVariants,
  useRuleActions,
} from '../useRuleActions/useRuleActions';
import { TsrctRuleVariant } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { useCurrentUser } from '~/hooks/useCurrentUser/useCurrentUser';
import { useRuleDetail } from '~/modules';

import { GET_RULE_DATA } from './Rule.query.gql';

type SupportedRuleVariant = (typeof supportedRuleVariants)[number];

export interface RuleVariantOption {
  value: SupportedRuleVariant;
  label: string;
}

export interface RuleSettingsData {
  id?: string; // Is empty on creation but set on updates
  name?: string; // Is empty on creation but set on updates
  responsibleUsers: string[];
  variant: SupportedRuleVariant;
  variantOptions: RuleVariantOption[];
}

export interface RuleSettingsBackendApi {
  loading: boolean;
  create: (data: RuleCreateInput) => Promise<string>;
  update: (data: RuleUpdateInput) => Promise<void>;
}

export const useRuleDialogActions = (): RuleSettingsData & RuleSettingsBackendApi => {
  const { ruleId } = useRuleDetail();
  const { userId } = useCurrentUser();
  const { onError } = useCustomSnackbar();
  const { data, loading } = useQuery(GET_RULE_DATA, { variables: { id: ruleId }, skip: !ruleId, onError });
  const { create, update, loading: processing } = useRuleActions();

  const { t } = useTranslation(I18nNamespace.ruleBuilder, { keyPrefix: 'RuleVariant' });

  const ruleSettings = useMemo(
    () => ({
      name: data?.tesseract.rule.name,
      responsibleUsers: data?.tesseract.rule.responsibleUsers ?? [userId],
      variant: data?.tesseract.rule.variant ?? TsrctRuleVariant.SOURCING,
    }),
    [data?.tesseract.rule.name, data?.tesseract.rule.responsibleUsers, data?.tesseract.rule.variant, userId],
  );

  const variantOptions = useMemo(
    () => supportedRuleVariants.map((variant) => ({ value: variant, label: t(variant) })),
    [t],
  );

  return useMemo(
    () => ({
      id: ruleId,
      ...ruleSettings,
      variantOptions,
      loading: loading || processing,
      update,
      create,
    }),
    [create, loading, processing, ruleId, ruleSettings, update, variantOptions],
  );
};
